import React, { useEffect, useState, useMemo } from "react";
import * as S from "./styles";
import {
	Checkbox,
	FormControlLabel,
	Slider,
	Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
	section: { marginBottom: 20 },
	chip: {
		margin: "0 4px 4px 0",
		height: 28,
		fontSize: 12,
	},
	chipActive: {
		backgroundColor: `${theme.palette.primary.main} !important`,
		color: "#fff !important",
	},
	dateInput: {
		border: "1px solid #ccc",
		borderRadius: 6,
		padding: "4px 8px",
		fontSize: 12,
		width: "100%",
		outline: "none",
		marginTop: 8,
		"&:focus": { borderColor: theme.palette.primary.main },
	},
	sliderLabel: { fontSize: 11, color: "#888", display: "flex", justifyContent: "space-between" },
	checkLabel: { fontSize: 13, color: "#444" },
	durationOpt: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		cursor: "pointer",
		fontSize: 13,
		color: "#444",
		padding: "2px 0",
	},
	radio: { accentColor: theme.palette.primary.main, cursor: "pointer" },
}));

const DATE_CHIPS = ["Hoje", "Amanhã", "Datas"];

const DURATION_OPTIONS = [
	{ label: "0 a 3 horas", min: 0, max: 180 },
	{ label: "3 a 7 horas", min: 180, max: 420 },
	{ label: "Dia inteiro (7h+)", min: 420, max: 1440 },
	{ label: "Vários dias", min: 1440, max: Infinity },
];

const DEFAULT_CATEGORIES = [
	"Excursões",
	"Visitas guiadas",
	"Passeios de barco",
	"Atividades ao ar livre",
	"Transporte e transfers",
	"City tour",
	"Trilhas e caminhadas",
	"Mergulho e snorkeling",
	"Praias e ilhas",
	"Gastronomia e vinhos",
];

const getCategoryName = (a) =>
	a.category?.name ||
	a.category?.plural ||
	a.category?.singular ||
	a.typologies?.[0]?.name ||
	null;

const CHARACTERISTICS = [
	{ label: "Cancelamento gratuito", key: "freeCancellation" },
	{ label: "Apenas atividades em português", key: "portuguese" },
	{ label: "Acessível para cadeirantes", key: "accessible" },
	{ label: "Recolha no Hotel", key: "hotelPickup" },
];

const EntertainmentFilter = ({ changeFilter, loading, activities = [] }) => {
	const classes = useStyles();

	// Disponibilidade
	const [selectedChip, setSelectedChip] = useState(null);
	const [dateFrom, setDateFrom] = useState("");

	// Hora de início
	const [startHour, setStartHour] = useState(0);

	// Price range derived from real activity points
	const points = useMemo(
		() => activities.map((a) => Number(a.pointsPrice)).filter(Boolean),
		[activities]
	);
	const minPriceAll = points.length ? Math.floor(Math.min(...points)) : 0;
	const maxPriceAll = points.length ? Math.ceil(Math.max(...points)) : 10000;
	const [priceRange, setPriceRange] = useState([minPriceAll, maxPriceAll]);

	// Reset price range when activities change
	useEffect(() => {
		setPriceRange([minPriceAll, maxPriceAll]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activities.length]);

	// Always show full default category list (filter works by title keyword search)
	const categoryOptions = DEFAULT_CATEGORIES;
	const [categories, setCategories] = useState([]);

	// Characteristics
	const [characteristics, setCharacteristics] = useState({});

	// Duration
	const [durationOption, setDurationOption] = useState(null);

	const toggleCategory = (name) => {
		setCategories((prev) =>
			prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
		);
	};

	const toggleCharacteristic = (key) => {
		setCharacteristics((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	useEffect(() => {
		changeFilter({
			categories,
			characteristics,
			priceRange,
			durationOption,
			dateFrom,
			startHour,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categories, characteristics, priceRange, durationOption, dateFrom, startHour]);

	if (loading) return <></>;

	return (
		<S.Wrapper>
			{/* Disponibilidade */}
			<div className={classes.section}>
				<S.FilterTitle>Disponibilidade</S.FilterTitle>
				<div style={{ display: "flex", flexWrap: "wrap", marginTop: 6 }}>
					{DATE_CHIPS.map((chip) => (
						<Chip
							key={chip}
							label={chip}
							size="small"
							className={`${classes.chip} ${selectedChip === chip ? classes.chipActive : ""}`}
							onClick={() => setSelectedChip(selectedChip === chip ? null : chip)}
							clickable
						/>
					))}
				</div>
				{selectedChip === "Datas" && (
					<input
						type="date"
						className={classes.dateInput}
						value={dateFrom}
						onChange={(e) => setDateFrom(e.target.value)}
					/>
				)}
			</div>

			{/* Hora de Início */}
			<div className={classes.section}>
				<S.FilterTitle>Hora de Início</S.FilterTitle>
				<Slider
					value={startHour}
					min={0}
					max={24}
					step={1}
					onChange={(_, val) => setStartHour(val)}
					valueLabelDisplay="auto"
					valueLabelFormat={(v) => `${v}h`}
					color="primary"
				/>
				<div className={classes.sliderLabel}>
					<span>0h</span>
					<span>24h</span>
				</div>
			</div>

			{/* Categorias */}
			<div className={classes.section}>
				<S.FilterTitle>Categorias</S.FilterTitle>
				{categoryOptions.map((name) => (
						<FormControlLabel
							key={name}
							label={<span className={classes.checkLabel}>{name}</span>}
							control={
								<Checkbox
									checked={categories.includes(name)}
									onChange={() => toggleCategory(name)}
									size="small"
									color="primary"
								/>
							}
							style={{ display: "flex", margin: 0 }}
						/>
					))}
			</div>

			{/* Características */}
			<div className={classes.section}>
				<S.FilterTitle>Características</S.FilterTitle>
				{CHARACTERISTICS.map(({ label, key }) => (
					<FormControlLabel
						key={key}
						label={<span className={classes.checkLabel}>{label}</span>}
						control={
							<Checkbox
								checked={!!characteristics[key]}
								onChange={() => toggleCharacteristic(key)}
								size="small"
								color="primary"
							/>
						}
						style={{ display: "flex", margin: 0 }}
					/>
				))}
			</div>

			{/* Preço */}
			{points.length > 1 && (
				<div className={classes.section}>
					<S.FilterTitle>Preço</S.FilterTitle>
					<Slider
						value={priceRange}
						min={minPriceAll}
						max={maxPriceAll}
						onChange={(_, val) => setPriceRange(val)}
						valueLabelDisplay="auto"
						valueLabelFormat={(v) => `${Math.round(v)} Pts`}
						color="primary"
					/>
					<div className={classes.sliderLabel}>
						<span>{Math.round(priceRange[0])} Pts</span>
						<span>{Math.round(priceRange[1])} Pts</span>
					</div>
				</div>
			)}

			{/* Duração */}
			<div className={classes.section}>
				<S.FilterTitle>Duração</S.FilterTitle>
				{DURATION_OPTIONS.map((opt) => (
					<label key={opt.label} className={classes.durationOpt}>
						<input
							type="radio"
							className={classes.radio}
							checked={durationOption?.label === opt.label}
							onChange={() =>
								setDurationOption(durationOption?.label === opt.label ? null : opt)
							}
						/>
						{opt.label}
					</label>
				))}
			</div>
		</S.Wrapper>
	);
};

export default EntertainmentFilter;
